<script setup>
import { ref, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import AnHeader from "./AnHeader.vue";
import AnStatCard from "./AnStatCard.vue";
import AnHourHeat from "./AnHourHeat.vue";
import AnSlaPlot from "./AnSlaPlot.vue";
import AnalyticsService from "@/services/analytics";
import AgentService from "@/services/agent";
import { useCompanyStore } from "@/stores/company";
import { resolveCurrentRange, previousRange, rangeToParams, computeDelta } from "@/utils/analyticsPeriod";

const companyStore = useCompanyStore();
const router = useRouter();

// ── state ───────────────────────────────────────────────────────
const agents = ref([]);
const selectedId = ref(null);
const period = ref("daily");
const dateRange = ref(null);
const loading = ref(false);

const stats = ref({ conversations: 0, avgPerDay: 0, activeDays: 0, uniqueUsers: 0 });
const convDelta = ref({ text: "", dir: "up" });
const hourValues = ref(Array(24).fill(0));
const topUsers = ref([]);
const sla = ref({ min: 0, median: 0, avg: 0, max: 0, pairs: 0 });

const COLORS = { accent: "#38bdf8", good: "#34d399", warn: "#fbbf24", accent2: "#8b7cf6" };
const PALETTE = [COLORS.accent, COLORS.good, COLORS.warn, COLORS.accent2, "#fb7185"];

// ── derived ─────────────────────────────────────────────────────

// ── fetch ────────────────────────────────────────────────────────
async function fetchAgents() {
  const companyId = companyStore.companyId;
  if (!companyId) return;
  try {
    const res = await AgentService.getAgentsGrid(companyId);
    agents.value = (res?.data || []).map((a) => ({ id: a.id, name: a.name }));
  } catch {
    // no-op
  }
}

async function fetchDeltas(companyId, agentId, params) {
  try {
    const current = resolveCurrentRange(period.value, dateRange.value);
    const previous = previousRange(current);
    const currentParams = { ...params, ...rangeToParams(current) };
    const prevParams = { ...params, ...rangeToParams(previous) };
    const [curRes, prevRes] = await Promise.all([
      AnalyticsService.getAgentAnalytics(companyId, agentId, currentParams),
      AnalyticsService.getAgentAnalytics(companyId, agentId, prevParams),
    ]);
    convDelta.value = computeDelta(
      curRes?.statistics?.total_conversations,
      prevRes?.statistics?.total_conversations,
    );
  } catch {
    // deltas are supplementary — leave them blank on failure
  }
}

async function fetchData() {
  const companyId = companyStore.companyId;
  if (!companyId || !selectedId.value) return;
  loading.value = true;
  convDelta.value = { text: "", dir: "up" };
  try {
    const params = { period: period.value };
    if (dateRange.value?.[0] && dateRange.value?.[1]) {
      params.from_date = dateRange.value[0].toISOString();
      params.until_date = dateRange.value[1].toISOString();
    }
    fetchDeltas(companyId, selectedId.value, params);
    const [convRes, msgRes] = await Promise.all([
      AnalyticsService.getAgentAnalytics(companyId, selectedId.value, params),
      AnalyticsService.getAgentMessagesAnalytics(companyId, selectedId.value, params),
    ]);

    if (convRes?.statistics) {
      const s = convRes.statistics;
      stats.value.conversations = s.total_conversations ?? 0;
      stats.value.avgPerDay = Number(s.avg_conversations_per_day ?? 0).toFixed(1);
      stats.value.activeDays = s.active_days ?? 0;
      stats.value.uniqueUsers = s.unique_users ?? 0;
    }

    if (convRes?.hourly_distribution?.length) {
      const filled = Array(24).fill(0);
      convRes.hourly_distribution.forEach((h) => { filled[h.hour] = h.conversation_count; });
      hourValues.value = filled;
    }

    if (convRes?.device_breakdown?.length) {
      const sorted = [...convRes.device_breakdown].sort((a, b) => b.conversation_count - a.conversation_count).slice(0, 5);
      topUsers.value = sorted.map((d, i) => ({
        name: d.device_name || (d.user_uuid || "").substring(0, 8) + "…",
        sub: `${d.conversation_count} convos`,
        value: d.conversation_count,
        color: PALETTE[i % PALETTE.length],
        uuid: d.user_uuid,
      }));
    } else {
      topUsers.value = [];
    }

    if (msgRes?.response_metrics) {
      const m = msgRes.response_metrics;
      sla.value = {
        min: Number(m.min_response_time_seconds ?? 0).toFixed(2),
        median: Number(m.median_response_time_seconds ?? 0).toFixed(2),
        avg: Number(m.avg_response_time_seconds ?? 0).toFixed(2),
        max: Number(m.max_response_time_seconds ?? 0).toFixed(2),
        pairs: m.response_pairs_analyzed ?? 0,
      };
    }
  } catch (e) {
    console.error("AgentsBoard fetch error:", e);
  } finally {
    loading.value = false;
  }
}

watch(selectedId, fetchData);
watch([period, dateRange], fetchData);

onMounted(async () => {
  loading.value = true;
  await fetchAgents();
  if (agents.value.length) {
    selectedId.value = agents.value[0].id; // triggers watcher → single fetchData call
  } else {
    loading.value = false;
  }
});
</script>

<template>
  <div class="an-page">
    <AnHeader
      title="Agents"
      subtitle="Per-agent performance, hourly demand patterns, and response time SLA."
      @period-change="p => { period = p.toLowerCase(); }"
      @date-change="dr => { dateRange = dr; }"
    />

    <!-- Agent selector -->
    <div class="an-agent-select">
      <button
        v-for="a in agents"
        :key="a.id"
        class="an-agent-btn"
        :class="{ 'is-active': selectedId === a.id }"
        @click="selectedId = a.id"
      >
        {{ a.name }}
      </button>
    </div>

    <!-- Skeleton -->
    <template v-if="loading">
      <div class="an-grid">
        <div v-for="i in 4" :key="i" class="an-panel an-c3">
          <div class="an-skel">
            <Skeletor height="10" width="55%" as="div" />
            <Skeletor height="32" width="50%" as="div" />
            <Skeletor height="10" width="35%" as="div" />
          </div>
        </div>
        <div class="an-panel an-c6"><div class="an-skel an-skel--chart"><Skeletor height="14" width="40%" as="div" /><Skeletor height="36" as="div" /></div></div>
        <div class="an-panel an-c6"><div class="an-skel"><Skeletor height="14" width="40%" as="div" /><Skeletor v-for="j in 5" :key="j" height="12" as="div" /></div></div>
        <div class="an-panel an-c12"><div class="an-skel an-skel--chart"><Skeletor height="14" width="30%" as="div" /><Skeletor height="50" as="div" /></div></div>
      </div>
    </template>

    <div v-else-if="!selectedId" class="an-empty">Select an agent above to view analytics.</div>

    <div v-else class="an-grid">
      <div class="an-panel an-c3">
        <AnStatCard
          label="Conversations"
          :value="stats.conversations.toLocaleString()"
          sub="all time"
          :delta="convDelta.text"
          :deltaDir="convDelta.dir"
        />
      </div>
      <div class="an-panel an-c3">
        <AnStatCard label="Avg / day" :value="stats.avgPerDay" :sub="`${stats.activeDays} active days`" />
      </div>
      <div class="an-panel an-c3">
        <AnStatCard label="Unique users" :value="stats.uniqueUsers.toLocaleString()" sub="reached" />
      </div>
      <div class="an-panel an-c3">
        <AnStatCard label="Avg response" :value="sla.avg" unit="s" :sub="`median ${sla.median}s`" />
      </div>

      <div class="an-panel an-c6">
        <div class="an-panel__head"><div class="an-panel__title">Hour-of-day demand</div></div>
        <AnHourHeat :values="hourValues" />
      </div>
      <div class="an-panel an-c6">
        <div class="an-panel__head"><div class="an-panel__title">Top devices</div></div>
        <div v-if="topUsers.length" class="an-agent-select">
          <button
            v-for="(u, i) in topUsers"
            :key="i"
            class="an-agent-btn"
            @click="u.uuid && router.push(`/analytics/devices/${u.uuid}`)"
          >
            {{ u.name }}
          </button>
        </div>
        <div v-else class="an-empty-panel">No device data available</div>
      </div>

      <div class="an-panel an-c12">
        <div class="an-panel__head"><div class="an-panel__title">Response time (SLA)</div></div>
        <div class="an-sla__chips" style="max-width:520px">
          <div class="an-sla__chip"><span>min</span><b>{{ sla.min }}s</b></div>
          <div class="an-sla__chip"><span>median</span><b>{{ sla.median }}s</b></div>
          <div class="an-sla__chip"><span>avg</span><b style="color:#38bdf8">{{ sla.avg }}s</b></div>
          <div class="an-sla__chip"><span>max</span><b>{{ sla.max }}s</b></div>
        </div>
        <AnSlaPlot
          :min="parseFloat(sla.min)"
          :median="parseFloat(sla.median)"
          :avg="parseFloat(sla.avg)"
          :max="parseFloat(sla.max)"
        />
        <div class="an-sla__foot">{{ Number(sla.pairs).toLocaleString() }} response pairs analyzed</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.an-empty {
  text-align: center;
  padding: 40px 0;
  color: var(--an-muted);
  font-size: 13px;
}
.an-empty-panel {
  color: var(--an-muted);
  font-size: 12px;
  padding: 16px 0;
}
</style>
